import { useState, useRef } from 'react';

/**
 * Dictionary of acronyms and technical terms used throughout the admin portal.
 * Every entry has a plain-Portuguese explanation for non-technical users.
 */
export const GLOSSARY: Record<string, string> = {
  MRR:    'MRR, Receita Mensal Recorrente: total de receita gerada por assinaturas ativas em um mês.',
  ARR:    'ARR, Receita Anual Recorrente: MRR × 12. Indica o tamanho anual do negócio de assinaturas.',
  ARPU:   'ARPU, Receita Média Por Usuário: total de receita ÷ número de usuários ativos. Mede quanto cada usuário vale em média.',
  LTV:    'LTV, Valor Vitalício do Cliente: receita total esperada de um usuário durante todo o tempo que ele usar o Professya.',
  ARPPU:  'ARPPU, Receita Média Por Usuário Pagante: mesmo que ARPU, mas considera apenas usuários que pagaram.',
  DAU:    'DAU, Usuários Ativos Diários: número de usuários distintos que acessaram o app em um dia.',
  MAU:    'MAU, Usuários Ativos Mensais: número de usuários distintos que acessaram o app em um mês.',
  DAU_MAU:'DAU/MAU, Índice de Engajamento: quanto maior (máx. 100%), mais frequentemente os usuários retornam ao app.',
  NPS:    'NPS, Net Promoter Score: índice de 0 a 100 que mede satisfação. Acima de 50 é ótimo, acima de 70 é excelente.',
  CTR:    'CTR, Taxa de Clique: % de pessoas que clicaram em um link ou botão em relação ao total que o viram.',
  CPC:    'CPC, Custo Por Clique: valor médio pago por cada clique em anúncio ou campanha paga.',
  CAC:    'CAC, Custo de Aquisição de Cliente: quanto você gasta em média para conquistar um novo usuário pagante.',
  CSAT:   'CSAT, Satisfação do Cliente (Customer Satisfaction Score): nota de satisfação dada pelo usuário após atendimento. Geralmente de 1 a 5.',
  SLA:    'SLA, Acordo de Nível de Serviço: prazo máximo comprometido para responder ou resolver uma solicitação.',
  LGPD:   'LGPD, Lei Geral de Proteção de Dados: lei brasileira (nº 13.709/2018) que regula coleta e uso de dados pessoais, semelhante ao GDPR europeu.',
  GDPR:   'GDPR, Regulamento Geral de Proteção de Dados: lei europeia de privacidade de dados que também se aplica a empresas que atendem cidadãos europeus.',
  DPO:    'DPO, Encarregado de Proteção de Dados (Data Protection Officer): responsável legal pela conformidade com a LGPD dentro da empresa.',
  DLQ:    'DLQ, Fila de Mensagens com Falha (Dead Letter Queue): lista de eventos que não puderam ser processados e precisam de atenção manual.',
  PIX:    'PIX, Sistema de pagamento instantâneo do Banco Central do Brasil, disponível 24h, 7 dias por semana.',
  BRL:    'BRL, Real Brasileiro. Código internacional da moeda do Brasil (ISO 4217).',
  USD:    'USD, Dólar Americano. Código internacional da moeda dos Estados Unidos (ISO 4217).',
  EUR:    'EUR, Euro. Código internacional da moeda da União Europeia (ISO 4217).',
  UTM:    'UTM, Parâmetros de rastreamento de campanha (Urchin Tracking Module): códigos adicionados ao final de URLs para identificar a origem de visitantes (ex: ?utm_source=instagram).',
  API:    'API, Interface de Programação de Aplicações: canal de comunicação entre sistemas. Ex: a API do Stripe processa pagamentos; a API do Claude gera textos com IA.',
  SSE:    'SSE, Server-Sent Events: tecnologia que permite o servidor enviar dados ao navegador em tempo real (usada nas previsões astrológicas geradas em streaming).',
  CSV:    'CSV, Comma-Separated Values: formato de planilha simples que pode ser aberto no Excel ou Google Sheets.',
  CMS:    'CMS, Sistema de Gestão de Conteúdo (Content Management System): painel para criar e publicar artigos e páginas sem precisar de código.',
  SEO:    'SEO, Otimização para Mecanismos de Busca (Search Engine Optimization): práticas que melhoram o posicionamento do site no Google.',
  HTML:   'HTML, Linguagem de marcação usada para estruturar páginas web. No contexto de e-mails, permite formatação rica (negrito, cores, links).',
  CDN:    'CDN, Rede de Distribuição de Conteúdo (Content Delivery Network): infraestrutura que entrega arquivos do site mais rápido ao distribuí-los em servidores pelo mundo.',
  RLS:    'RLS, Segurança em Nível de Linha (Row Level Security): controle de acesso do banco de dados que determina quais linhas cada usuário pode ver ou modificar.',
  p50:    'p50, Percentil 50 (mediana): metade das chamadas respondeu abaixo deste tempo. Representa o desempenho típico.',
  p95:    'p95, Percentil 95: 95% das chamadas responderam abaixo deste tempo. Representa o pior caso usual (exclui outliers extremos).',
  p99:    'p99, Percentil 99: 99% das chamadas responderam abaixo deste tempo. Detecta problemas sérios de lentidão.',
  GC:     'Gift Card, Vale-presente digital que permite presentear alguém com acesso ao Professya.',
  ARPA:   'ARPA, Receita Média Por Conta: semelhante ao ARPU, mas contabiliza por conta (empresa ou família) em vez de usuário individual.',
  CHURN:  'Churn, Taxa de cancelamento: % de usuários pagantes que encerraram a assinatura em um período.',
};

interface GlossaryTermProps {
  term: string;
  children: React.ReactNode;
  /** Override the definition from GLOSSARY */
  definition?: string;
}

/**
 * Wraps a technical term with an underline and tooltip showing its definition.
 * Usage: <GlossaryTerm term="MRR">MRR</GlossaryTerm>
 */
export function GlossaryTerm({ term, children, definition }: GlossaryTermProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const desc = definition ?? GLOSSARY[term];
  if (!desc) return <>{children}</>;

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span
        style={{
          borderBottom: '1px dashed var(--petrol)',
          cursor: 'help',
          color: 'inherit',
        }}
      >
        {children}
      </span>
      {visible && (
        <span
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '6px',
            zIndex: 9999,
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '6px',
            padding: '0.5rem 0.75rem',
            fontSize: '11px',
            color: 'var(--text-secondary)',
            lineHeight: 1.55,
            maxWidth: '280px',
            whiteSpace: 'normal',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
          }}
        >
          {desc}
          {/* Caret */}
          <span style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid var(--border-subtle)',
          }} />
        </span>
      )}
    </span>
  );
}

/** Quick inline badge for a metric with tooltip */
interface MetricBadgeProps {
  label: string;
  value: string | number;
  term?: string;
  definition?: string;
  color?: string;
  sub?: string;
}

export function MetricBadge({ label, value, term, definition, color, sub }: MetricBadgeProps) {
  const wrappedLabel = term
    ? <GlossaryTerm term={term} definition={definition}>{label}</GlossaryTerm>
    : label;

  return (
    <div style={{
      padding: '1rem 1.25rem',
      backgroundColor: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-card)',
      minWidth: '140px',
      flex: '1 1 140px',
    }}>
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.35rem' }}>
        {wrappedLabel}
      </p>
      <p style={{ fontSize: '1.625rem', fontWeight: 700, color: color ?? 'var(--text-primary)', fontFamily: 'var(--font-display)', margin: 0, lineHeight: 1 }}>
        {value}
      </p>
      {sub && <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>{sub}</p>}
    </div>
  );
}

/** Inline usage: wrap a standalone acronym in a page */
export function Term({ k, children }: { k: string; children?: React.ReactNode }) {
  return <GlossaryTerm term={k}>{children ?? k}</GlossaryTerm>;
}
